import type {
  AccessField,
  Binary,
  Bool,
  CalendarValue,
  Call,
  Convert,
  Expression,
  ExpressionVisitor,
  Group,
  Identifier,
  Negate,
  Num,
  Str,
} from './ast.ts';
import { Tokenizer } from './tokenizer.ts';
import { Parser } from './parser.ts';

export class VariableNameFinder implements ExpressionVisitor<void> {
  static findInExpression(expression: Expression): Set<string> {
    const variablesFound = new Set<string>();
    // Users of findInExpression are always looking for bare variable names, not annotated names:
    const finder = new VariableNameFinder((identifier) => variablesFound.add(identifier.nameBare));
    expression.accept(finder);
    return variablesFound;
  }

  static findIdentifiersInExpression(expression: Expression): Identifier[] {
    const identifiersFound = new Map<string, Identifier>();
    expression.accept(
      new VariableNameFinder((identifier) => {
        if (!identifiersFound.has(identifier.nameWithAnnotation)) {
          identifiersFound.set(identifier.nameWithAnnotation, identifier);
        }
      }),
    );
    return Array.from(identifiersFound.values());
  }

  static findInString(
    formula: string,
  ): { status: 'error'; error: string } | { status: 'success'; variableNames: Set<string>; expression: Expression } {
    const tokensOrError = Tokenizer.tokenize(formula);
    if ('error' in tokensOrError) {
      return { status: 'error', error: `Unable to tokenize formula, error was: ${tokensOrError.error}` };
    }

    const parsedExpressionOrError = Parser.parseExpression(tokensOrError.tokens);
    if ('error' in parsedExpressionOrError) {
      return {
        status: 'error',
        error: `Unable to parse formula "${formula}", error was: ${parsedExpressionOrError.error}`,
      };
    }

    return {
      status: 'success',
      variableNames: VariableNameFinder.findInExpression(parsedExpressionOrError.expression),
      expression: parsedExpressionOrError.expression,
    };
  }

  static findIdentifiersInString(
    formula: string,
  ): { status: 'error'; error: string } | { status: 'success'; identifiers: Identifier[]; expression: Expression } {
    const tokensOrError = Tokenizer.tokenize(formula);
    if ('error' in tokensOrError) {
      return { status: 'error', error: `Unable to tokenize formula, error was: ${tokensOrError.error}` };
    }

    const parsedExpressionOrError = Parser.parseExpression(tokensOrError.tokens);
    if ('error' in parsedExpressionOrError) {
      return {
        status: 'error',
        error: `Unable to parse formula "${formula}", error was: ${parsedExpressionOrError.error}`,
      };
    }

    return {
      status: 'success',
      identifiers: VariableNameFinder.findIdentifiersInExpression(parsedExpressionOrError.expression),
      expression: parsedExpressionOrError.expression,
    };
  }

  static findInStringIgnoringErrors(formula: string): Set<string> {
    const found = VariableNameFinder.findInString(formula);
    if (found.status === 'success') {
      return found.variableNames;
    } else {
      return new Set();
    }
  }

  static findInStringsIgnoringErrors(formulas: string[]): Set<string> {
    const result = new Set<string>();

    for (const formula of formulas) {
      const found = VariableNameFinder.findInString(formula);
      if (found.status === 'success') {
        for (const varName of found.variableNames) {
          result.add(varName);
        }
      }
    }

    return result;
  }

  onIdentifier: (identifier: Identifier) => void;

  private constructor(onIdentifier: (identifier: Identifier) => void) {
    this.onIdentifier = onIdentifier;
  }

  visitNum(_node: Num): void {}

  visitStr(_node: Str): void {}

  visitBool(_node: Bool): void {}

  visitCalendarValue(_node: CalendarValue): void {}

  visitIdentifier(node: Identifier): void {
    this.onIdentifier(node);
  }

  visitAccessField(node: AccessField): void {
    node.expr.accept(this);
  }

  visitNegate(node: Negate): void {
    node.expr.accept(this);
  }

  visitCall(node: Call): void {
    node.args.map((arg) => arg.accept(this));
    for (const arg of node.args) {
      arg.accept(this);
    }
  }

  visitGroup(node: Group): void {
    node.expr.accept(this);
  }

  visitConvert(node: Convert): void {
    node.expr.accept(this);
  }

  visitBinary(node: Binary): void {
    node.left.accept(this);
    node.right.accept(this);
  }
}
